import { ORG_EXPECTED_FILES_WITH_HEADERS } from '../ExpectedFilesList';
import Papa from 'papaparse';

/**
 * Reads CSV headers from a file using Papa Parse
 * @param {File} file - The file to read headers from
 * @returns {Promise<string[]>} Array of header strings
 */
export const readCSVHeaders = (file) => {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: false,
            preview: 1, // Only read the first row
            skipEmptyLines: true,
            complete: (results) => {
                if (results.data && results.data[0]) {
                    // Get the first row as headers, filter out empty strings
                    const headers = results.data[0].filter(header => header && header.trim() !== '');
                    resolve(headers);
                } else {
                    resolve([]);
                }
            },
            error: (error) => {
                reject(error);
            }
        });
    });
};

/**
 * Validates source file headers against expected file types for an organization
 * @param {File} file - The file to validate
 * @param {string} organizationId - The organization ID
 * @returns {Promise<{isValid: boolean, matchingFileType: string|null}>}
 */
export const validateSourceHeaders = async (file, organizationId) => {
    try {
        const fileHeaders = await readCSVHeaders(file);
        const expectedFiles = ORG_EXPECTED_FILES_WITH_HEADERS[organizationId];
        
        // If no expected files defined for this organization, allow any headers
        if (!expectedFiles) {
            return { isValid: true, matchingFileType: null };
        }

        // Check if headers match any expected file type
        for (const [fileType, expectedHeaders] of Object.entries(expectedFiles)) {
            // Compare headers (order doesn't matter)
            const headersMatch = fileHeaders.length === expectedHeaders.length &&
                fileHeaders.every(header => expectedHeaders.includes(header));
            
            if (headersMatch) {
                return { isValid: true, matchingFileType: fileType };
            }
        }

        return { isValid: false, matchingFileType: null };
    } catch (error) {
        console.error('Error reading file headers:', error);
        return { isValid: false, matchingFileType: null };
    }
};

/**
 * Validates augmentation file headers against existing augmentation files
 * @param {File} file - The file to validate
 * @param {Array} existingAugmentationFiles - Array of existing augmentation files
 * @returns {Promise<{isValid: boolean, matchingFile: object|null}>}
 */
export const validateAugmentationHeaders = async (file, existingAugmentationFiles) => {
    try {
        const newFileHeaders = await readCSVHeaders(file);
        
        // If no existing augmentation files, allow any headers
        if (existingAugmentationFiles.length === 0) {
            return { isValid: true, matchingFile: null };
        }

        // Check if headers match any existing augmentation file
        for (const existingFile of existingAugmentationFiles) {
            if (existingFile.header) {
                // Parse existing file header using Papa Parse
                const parseResult = Papa.parse(existingFile.header, {
                    header: false,
                    skipEmptyLines: true
                });
                
                if (parseResult.data && parseResult.data[0]) {
                    const existingHeaders = parseResult.data[0].filter(header => header && header.trim() !== '');
                    
                    // Compare headers (order doesn't matter)
                    const headersMatch = newFileHeaders.length === existingHeaders.length &&
                        newFileHeaders.every(header => existingHeaders.includes(header));
                    
                    if (headersMatch) {
                        return { isValid: true, matchingFile: existingFile };
                    }
                }
            }
        }

        return { isValid: false, matchingFile: null };
    } catch (error) {
        console.error('Error reading file headers:', error);
        return { isValid: false, matchingFile: null };
    }
};